import React, { useEffect, useState } from 'react'
import { Col, Row, Form } from 'react-bootstrap'
import { BaseButton } from 'iebt-zing-storybook'
import { trans } from 'sharedKernel/i18n'
import { showToast } from 'components/toast'
import { api, postProvider, PostFormat } from 'sharedKernel/api'
import { useSelector } from 'react-redux'
import { TReducers } from 'store'
import { caixaUser } from 'sharedKernel/user/CaixaUser'
import { FormTitle } from '../styles'
import { SecuritySettingsContainer } from './styles'

export const SecuritySettingsForm = (): JSX.Element | null => {
  const userAuth = useSelector((state: TReducers) => state.auth)
  const [is2FAEnabled, setIs2FAEnabled] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const { mutate } = postProvider()

  const labels = {
    title: trans('Configurações de Segurança'),
    twoFactorTitle: trans('Autenticação de Dois Fatores'),
    twoFactorDescription: trans('Adicione uma camada extra de segurança à sua conta'),
    twoFactorEnabled: trans('Ativada'),
    twoFactorDisabled: trans('Desativada'),
    enable2FA: trans('Ativar 2FA'),
    disable2FA: trans('Desativar 2FA'),
    loading: trans('Carregando...'),
    successEnable: trans('Autenticação de dois fatores ativada com sucesso'),
    successDisable: trans('Autenticação de dois fatores desativada com sucesso'),
    errorEnable: trans('Erro ao ativar autenticação de dois fatores'),
    errorDisable: trans('Erro ao desativar autenticação de dois fatores'),
    errorLoad: trans('Erro ao carregar configurações de segurança')
  }

  // Verificar se é funcionário da Caixa
  const isCaixaEmployee = caixaUser.isCaixaUser()

  // Carregar status atual do 2FA
  useEffect(() => {
    const load2FAStatus = async () => {
      if (!isCaixaEmployee) return
      
      try {
        setIsLoading(true)
        const response = await api.get('/v1/system/caixa-employee/2fa-status')
        if (response.status === 200) {
          setIs2FAEnabled(response.data.data?.is2FAEnabled || false)
        }
      } catch (error) {
        console.error('Erro ao carregar status do 2FA:', error)
        showToast('error', labels.errorLoad)
      } finally {
        setIsLoading(false)
        setIsInitialized(true)
      }
    }

    load2FAStatus()
  }, [isCaixaEmployee])

  // Função para alternar 2FA
  const handleToggle2FA = async () => {
    if (!isCaixaEmployee) return

    try {
      setIsLoading(true)
      
      const endpoint = is2FAEnabled ? '/v1/system/caixa-employee/2fa/disable' : '/v1/system/caixa-employee/2fa/enable'
      
      const request: PostFormat = {
        url: endpoint,
        data: { data: {} }
      }

      mutate(request, {
        onSuccess: (response) => {
          const newStatus = !is2FAEnabled
          setIs2FAEnabled(newStatus)
          
          if (newStatus) {
            showToast('success', labels.successEnable)
          } else {
            showToast('success', labels.successDisable)
          }
        },
        onError: (error) => {
          console.error('Erro ao alterar 2FA:', error)
          const errorMessage = is2FAEnabled ? labels.errorDisable : labels.errorEnable
          showToast('error', error.response?.data?.message || errorMessage)
        },
        onSettled: () => {
          setIsLoading(false)
        }
      })
    } catch (error) {
      console.error('Erro ao alterar 2FA:', error)
      const errorMessage = is2FAEnabled ? labels.errorDisable : labels.errorEnable
      showToast('error', errorMessage)
      setIsLoading(false)
    }
  }

  // Se não for funcionário da Caixa, não renderizar nada
  if (!isCaixaEmployee) {
    return null
  }

  return (
    <SecuritySettingsContainer>
      <FormTitle>
        <h1>{labels.title}</h1>
      </FormTitle>
      
      <Row className="mb-4">
        <Col sm={12}>
          <div className="security-setting-item">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">{labels.twoFactorTitle}</h5>
                <p className="text-muted mb-0">{labels.twoFactorDescription}</p>
                <small className={`badge ${is2FAEnabled ? 'bg-success' : 'bg-secondary'}`}>
                  {is2FAEnabled ? labels.twoFactorEnabled : labels.twoFactorDisabled}
                </small>
              </div>
              <div className="d-flex align-items-center">
                <div className="custom-toggle">
                  <input
                    type="checkbox"
                    id="2fa-toggle"
                    checked={is2FAEnabled}
                    onChange={handleToggle2FA}
                    disabled={isLoading || !isInitialized}
                    className="toggle-input"
                  />
                  <label htmlFor="2fa-toggle" className="toggle-label">
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {isLoading && (
        <Row>
          <Col sm={12} className="text-center">
            <div className="d-flex align-items-center justify-content-center">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mb-0">{labels.loading}</p>
            </div>
          </Col>
        </Row>
      )}
    </SecuritySettingsContainer>
  )
}
